import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { notificationService } from '../../../services/notificationService';
import { fetchBankClients, BankClientData } from '../../manage_clients/bank_clients/service/bankClientService';

export type ComplainStatus =
  | 'open'
  | 'in_progress'
  | 'resolved'
  | 'rejected'
  | 'completed'
  | 'closed';

export interface Complain {
  id: string;
  companyId: string;
  bankId: string;
  branchId: string;
  bankName?: string;
  address?: string;
  branchCode?: string;
  type: string;
  description: string;
  status: ComplainStatus;
  /** Set when branch rejects after company marked completed (public portal). */
  rejectionReason?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
  /** Set when status becomes `completed` (company or workflow). */
  completedAt?: unknown;
  /** Set when status becomes `resolved` (branch public portal). */
  resolvedAt?: unknown;
}

export const COMPLAIN_STATUSES: ComplainStatus[] = [
  'open',
  'in_progress',
  'resolved',
  'rejected',
  'completed',
  'closed',
];

/** Statuses the company dashboard may assign (branch sets resolved / rejected from the public portal). */
export const COMPANY_COMPLAIN_STATUSES: ComplainStatus[] = [
  'open',
  'in_progress',
  'completed',
  'closed',
];

function titleCaseStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatComplainStatusLabel(status: ComplainStatus | string): string {
  return titleCaseStatus(String(status));
}

export type ComplainListUnsubscribe = () => void;

export function subscribeComplainsForBranch(
  companyId: string,
  bankId: string,
  branchId: string,
  onData: (rows: Complain[]) => void,
  onError?: (error: Error) => void
): ComplainListUnsubscribe {
  const col = complainsCollectionRef(companyId, bankId, branchId);
  return onSnapshot(
    col,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Complain, 'id'>) }));
      rows.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
      onData(rows);
    },
    (err) => onError?.(err as Error)
  );
}

export function subscribeAllComplainsForCompany(
  companyId: string,
  onData: (rows: Complain[]) => void,
  onError?: (error: Error) => void
): ComplainListUnsubscribe {
  let cancelled = false;
  const unsubscribers: ComplainListUnsubscribe[] = [];
  const cache = new Map<string, Complain[]>();

  const emit = () => {
    if (cancelled) return;
    const flat = Array.from(cache.values()).flat();
    flat.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
    onData(flat);
  };

  fetchBankClients(companyId)
    .then((branches) => {
      if (cancelled) return;
      if (branches.length === 0) {
        onData([]);
        return;
      }
      branches.forEach((b) => {
        const key = `${b.bankId}_${b.branchId}`;
        const col = complainsCollectionRef(companyId, b.bankId, b.branchId);
        const unsub = onSnapshot(
          col,
          (snap) => {
            const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Complain, 'id'>) }));
            cache.set(key, rows);
            emit();
          },
          (err) => {
            if (!cancelled) onError?.(err as Error);
          }
        );
        unsubscribers.push(unsub);
      });
    })
    .catch((e) => {
      if (!cancelled) onError?.(e as Error);
    });

  return () => {
    cancelled = true;
    unsubscribers.forEach((u) => u());
    unsubscribers.length = 0;
    cache.clear();
  };
}

/** Complains live under the company document tree (not bankClient). */
export function complainsCollectionRef(companyId: string, bankId: string, branchId: string) {
  return collection(db, 'companies', companyId, 'banks', bankId, 'branches', branchId, 'complains');
}

export function complainDocRef(companyId: string, bankId: string, branchId: string, complainId: string) {
  return doc(db, 'companies', companyId, 'banks', bankId, 'branches', branchId, 'complains', complainId);
}

/** Public IDs: BRANCHCODE-ABC-123 or BANKNAME-ABC-123 when branch code missing */
export function slugSegmentForComplainId(raw: string): string {
  let s = String(raw).trim().toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  if (!s) s = 'CLIENT';
  return s.slice(0, 32);
}

export function buildPublicComplainId(params: {
  branchCode?: string;
  bankName?: string;
  address?: string;
}): string {
  const hasBranch = params.branchCode != null && String(params.branchCode).trim() !== '';
  const prefix = hasBranch
    ? slugSegmentForComplainId(String(params.branchCode))
    : slugSegmentForComplainId(params.bankName || params.address || 'CLIENT');
  const n = Math.floor(100 + Math.random() * 900);
  return `${prefix}-ABC-${n}`;
}

async function generateUniqueComplainDocId(
  companyId: string,
  bankId: string,
  branchId: string,
  params: { branchCode?: string; bankName?: string; address?: string }
): Promise<string> {
  const col = complainsCollectionRef(companyId, bankId, branchId);
  for (let i = 0; i < 12; i++) {
    const id = buildPublicComplainId(params);
    const snap = await getDoc(doc(col, id));
    if (!snap.exists()) return id;
  }
  return `${buildPublicComplainId(params)}-${Date.now()}`;
}

function toMillis(v: unknown): number {
  if (!v) return 0;
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate().getTime();
  }
  if (v instanceof Date) return v.getTime();
  return 0;
}

export function complainDateToJs(v: unknown): Date | null {
  if (!v) return null;
  try {
    if (typeof (v as { toDate?: () => Date }).toDate === 'function') {
      return (v as { toDate: () => Date }).toDate();
    }
    if (v instanceof Date) return v;
  } catch {
    return null;
  }
  return null;
}

/** e.g. September 27 - 2026, */
export function formatComplainDisplayDate(v: unknown): string {
  const d = complainDateToJs(v);
  if (!d) return '—';
  const month = d.toLocaleString('en-US', { month: 'long' });
  return `${month} ${d.getDate()} - ${d.getFullYear()},`;
}

/** For detail UI: prefer stored `completedAt`, else `updatedAt` while status is completed. */
export function effectiveCompletedAtForDetail(c: Complain): unknown {
  if (complainDateToJs(c.completedAt)) return c.completedAt;
  if (c.status === 'completed') return c.updatedAt;
  return null;
}

/** For detail UI: prefer stored `resolvedAt`, else `updatedAt` while status is resolved. */
export function effectiveResolvedAtForDetail(c: Complain): unknown {
  if (complainDateToJs(c.resolvedAt)) return c.resolvedAt;
  if (c.status === 'resolved') return c.updatedAt;
  return null;
}

export async function createComplain(params: {
  companyId: string;
  bankId: string;
  branchId: string;
  bankName?: string;
  address?: string;
  branchCode?: string;
  type: string;
  description: string;
}): Promise<string> {
  const { companyId, bankId, branchId, bankName, address, branchCode, type, description } = params;
  const id = await generateUniqueComplainDocId(companyId, bankId, branchId, {
    branchCode,
    bankName,
    address,
  });
  const ref = complainDocRef(companyId, bankId, branchId, id);
  await setDoc(ref, {
    companyId,
    bankId,
    branchId,
    bankName: bankName ?? '',
    address: address ?? '',
    branchCode: branchCode != null ? String(branchCode) : '',
    type: type.trim(),
    description: description.trim(),
    status: 'open' as ComplainStatus,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  const branchLabel = [bankName, address].filter((x) => x != null && String(x).trim() !== '').join(' — ') || 'Branch';
  void notificationService.sendComplainCreatedNotification(companyId, {
    complainId: id,
    complainType: type.trim(),
    branchLabel,
    descriptionPreview: description.trim(),
  });
  return id;
}

export async function fetchComplainsForBranch(
  companyId: string,
  bankId: string,
  branchId: string
): Promise<Complain[]> {
  const col = complainsCollectionRef(companyId, bankId, branchId);
  const snap = await getDocs(col);
  const rows = snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<Complain, 'id'>),
  }));
  rows.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
  return rows;
}

export async function fetchAllComplainsForCompany(companyId: string): Promise<Complain[]> {
  const branches: BankClientData[] = await fetchBankClients(companyId);
  const batches = await Promise.all(
    branches.map((b) => fetchComplainsForBranch(companyId, b.bankId, b.branchId))
  );
  const flat = batches.flat();
  flat.sort((a, b) => {
    const ta = toMillis(a.createdAt);
    const tb = toMillis(b.createdAt);
    return tb - ta;
  });
  return flat;
}

export async function fetchComplainById(companyId: string, complainId: string): Promise<Complain | null> {
  const branches = await fetchBankClients(companyId);
  for (const b of branches) {
    const ref = complainDocRef(companyId, b.bankId, b.branchId, complainId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      return { id: snap.id, ...(snap.data() as Omit<Complain, 'id'>) };
    }
  }
  return null;
}

export async function updateComplainStatus(
  companyId: string,
  bankId: string,
  branchId: string,
  complainId: string,
  status: ComplainStatus,
  options?: { rejectionReason?: string | null; statusChangeActor?: 'company' | 'public' }
): Promise<void> {
  const ref = complainDocRef(companyId, bankId, branchId, complainId);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    throw new Error('Complain not found');
  }
  const prev = snap.data() as Omit<Complain, 'id'>;
  const previousStatus = prev.status as ComplainStatus;
  const actor = options?.statusChangeActor ?? 'company';

  const patch: Record<string, unknown> = {
    status,
    updatedAt: serverTimestamp(),
  };
  if (status === 'completed') {
    patch.completedAt = serverTimestamp();
  }
  if (status === 'resolved') {
    patch.resolvedAt = serverTimestamp();
  }
  if (status === 'rejected') {
    patch.rejectionReason =
      options?.rejectionReason != null && String(options.rejectionReason).trim() !== ''
        ? String(options.rejectionReason).trim()
        : '';
  } else {
    patch.rejectionReason = deleteField();
  }
  await updateDoc(ref, patch as Record<string, never>);

  if (previousStatus !== status) {
    const branchLabel =
      [prev.bankName, prev.address].filter((x) => x != null && String(x).trim() !== '').join(' — ') || 'Branch';
    const complainType = String(prev.type || '').trim() || 'Complain';
    void notificationService.sendComplainStatusChangeNotification({
      companyId,
      bankId,
      branchId,
      complainId,
      previousStatus,
      newStatus: status,
      complainType,
      branchLabel,
      actor,
    });
  }
}
